'use client'
import LinkComponent from '@components/LinkComponent'
import Image from 'next/image'

// ─── helpers ──────────────────────────────────────────────────────────────────

const buildOverlay = (s) => {
  if (!s.overlayEnabled) return null
  if (s.overlayType === 'gradient') {
    const dir = s.overlayGradientDir ?? 'to top'
    const c1  = s.overlayGradientC1  ?? 'rgba(0,0,0,0)'
    const c2  = s.overlayGradientC2  ?? 'rgba(0,0,0,0.7)'
    return `linear-gradient(${dir}, ${c1}, ${c2})`
  }
  return s.overlayColor ?? 'rgba(0,0,0,0.35)'
}

const resolveAnchor = (anchor, ox = 16, oy = 16) => {
  const base = { position: 'absolute', zIndex: 20, display: 'flex', flexDirection: 'column' }
  const x = `${ox}px`, y = `${oy}px`
  switch (anchor) {
    case 'top-left':   return { ...base, top: y, left: x,      alignItems: 'flex-start' }
    case 'top-center': return { ...base, top: y, left: '50%',  transform: 'translateX(-50%)', alignItems: 'center' }
    case 'top-right':  return { ...base, top: y, right: x,     alignItems: 'flex-end' }
    case 'mid-left':   return { ...base, top: '50%', left: x,  transform: 'translateY(-50%)', alignItems: 'flex-start' }
    case 'mid-center': return { ...base, top: '50%', left: '50%', transform: 'translate(-50%,-50%)', alignItems: 'center' }
    case 'mid-right':  return { ...base, top: '50%', right: x, transform: 'translateY(-50%)', alignItems: 'flex-end' }
    case 'bot-left':   return { ...base, bottom: y, left: x,   alignItems: 'flex-start' }
    case 'bot-center': return { ...base, bottom: y, left: '50%', transform: 'translateX(-50%)', alignItems: 'center' }
    case 'bot-right':  return { ...base, bottom: y, right: x,  alignItems: 'flex-end' }
    default:           return { ...base, bottom: y, left: x,   alignItems: 'flex-start' }
  }
}

// ─── DistrictCard ─────────────────────────────────────────────────────────────
// cfg shape — all keys optional, sane defaults applied:
// Card:       aspectRatio, cardRadius, cardShadow, cardBorderWidth, cardBorderColor
//             hoverScale (e.g. 1.03), hoverShadow
// Overlay:    overlayEnabled, overlayType ('solid'|'gradient'),
//             overlayColor, overlayGradientDir, overlayGradientC1, overlayGradientC2
// Text pos:   textAnchor ('bot-left'|…), textOffsetX, textOffsetY, textGap
// Title:      showTitle, titleColor, titleSize, titleWeight, titleLineHeight,
//             titleLetterSpacing, titleTextTransform, titleShadow,
//             titleBg, titleBgRadius, titlePadX, titlePadY, titleBlur
// Subtitle:   showSubtitle, subtitleColor, subtitleSize, subtitleWeight,
//             subtitleLetterSpacing, subtitleTextTransform, subtitleShadow,
//             subtitleBg, subtitleBgRadius, subtitlePadX, subtitlePadY, subtitleBlur

export const DistrictCard = ({ district, cfg = {}, lang = 'en' }) => {
  const s = cfg
  const overlay = buildOverlay(s)

  const label    = district.label ?? district.subdistrictName?.[lang] ?? ''
  const location = district.location ?? ''

  const cardStyle = {
    position: 'relative',
    width: '100%',
    cursor: district.slug ? 'pointer' : 'default',
    borderRadius: s.cardRadius ?? '0.5rem',
    overflow: 'hidden',
    boxShadow: s.cardShadow ?? 'none',
    border: s.cardBorderWidth
      ? `${s.cardBorderWidth}px solid ${s.cardBorderColor ?? 'transparent'}`
      : 'none',
    transition: s.hoverScale ? 'transform 0.25s ease, box-shadow 0.25s ease' : undefined,
  }

  const imageContainerStyle = {
    position: 'relative',
    aspectRatio: s.aspectRatio ?? '3 / 4',
    overflow: 'hidden',
  }

  const textWrapperStyle = {
    ...resolveAnchor(s.textAnchor ?? 'bot-left', s.textOffsetX, s.textOffsetY),
    gap: `${s.textGap ?? 4}px`,
  }

  const titleStyle = {
    margin: 0,
    lineHeight: s.titleLineHeight ?? 1.2,
    display: 'inline-block',
    color: s.titleColor ?? '#ffffff',
    fontSize: `${s.titleSize ?? 1.1}rem`,
    fontWeight: s.titleWeight ?? '700',
    letterSpacing: s.titleLetterSpacing ? `${s.titleLetterSpacing}em` : undefined,
    textTransform: s.titleTextTransform ?? 'none',
    textShadow: s.titleShadow,
    background: s.titleBg && s.titleBg !== 'transparent' ? s.titleBg : undefined,
    borderRadius: s.titleBgRadius ? `${s.titleBgRadius}px` : undefined,
    padding: s.titleBg && s.titleBg !== 'transparent'
      ? `${s.titlePadY ?? 3}px ${s.titlePadX ?? 8}px`
      : undefined,
    backdropFilter: s.titleBlur ? `blur(${s.titleBlur}px)` : undefined,
    WebkitBackdropFilter: s.titleBlur ? `blur(${s.titleBlur}px)` : undefined,
  }

  const subtitleStyle = {
    margin: 0,
    display: 'inline-block',
    color: s.subtitleColor ?? 'rgba(255,255,255,0.8)',
    fontSize: `${s.subtitleSize ?? 0.8}rem`,
    fontWeight: s.subtitleWeight ?? '400',
    letterSpacing: s.subtitleLetterSpacing ? `${s.subtitleLetterSpacing}em` : undefined,
    textTransform: s.subtitleTextTransform ?? 'none',
    textShadow: s.subtitleShadow,
    background: s.subtitleBg && s.subtitleBg !== 'transparent' ? s.subtitleBg : undefined,
    borderRadius: s.subtitleBgRadius ? `${s.subtitleBgRadius}px` : undefined,
    padding: s.subtitleBg && s.subtitleBg !== 'transparent'
      ? `${s.subtitlePadY ?? 2}px ${s.subtitlePadX ?? 6}px`
      : undefined,
    backdropFilter: s.subtitleBlur ? `blur(${s.subtitleBlur}px)` : undefined,
    WebkitBackdropFilter: s.subtitleBlur ? `blur(${s.subtitleBlur}px)` : undefined,
  }

  const card = (
    <div
      style={cardStyle}
      onMouseEnter={s.hoverScale ? e => {
        e.currentTarget.style.transform = `scale(${s.hoverScale})`
        if (s.hoverShadow) e.currentTarget.style.boxShadow = s.hoverShadow
      } : undefined}
      onMouseLeave={s.hoverScale ? e => {
        e.currentTarget.style.transform = 'scale(1)'
        e.currentTarget.style.boxShadow = s.cardShadow ?? 'none'
      } : undefined}
    >
      <div style={imageContainerStyle}>
        {overlay && (
          <div style={{
            position: 'absolute', inset: 0,
            background: overlay,
            zIndex: 10,
            pointerEvents: 'none',
          }} />
        )}

        <Image
          src={district.coverImage}
          alt={label}
          fill
          className="object-cover"
          placeholder={district.blur ? 'blur' : 'empty'}
          blurDataURL={district.blur || undefined}
        />

        {(s.showTitle !== false || s.showSubtitle !== false) && (
          <div style={textWrapperStyle}>
            {s.showTitle !== false && label && <p style={titleStyle}>{label}</p>}
            {s.showSubtitle !== false && location && <p style={subtitleStyle}>{location}</p>}
          </div>
        )}
      </div>
    </div>
  )

  if (district.slug) {
    return (
      <LinkComponent href={`/properties?district=${district.value}`}>
        {card}
      </LinkComponent>
    )
  }

  return card
}

// ─── DistrictCardSlider ───────────────────────────────────────────────────────

import { useState, useEffect } from 'react'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Navigation } from 'swiper/modules'
import 'swiper/css'
import 'swiper/css/navigation'

const DistrictCardSlider = ({ title, description, districtSlider, districts, url, lang = 'en' }) => {
  const [mounted, setMounted] = useState(false)
  useEffect(() => { setMounted(true) }, [])

  const carousel  = districtSlider ?? {}
  const cfg       = carousel.cardConfig   ?? {}
  const sliderCfg = carousel.sliderConfig ?? { mobile: 1.2, tablet: 3, desktop: 5 }

  const titleEl = title?.[lang]
    ? url
      ? <LinkComponent href={`/${url}`}><h2 className="carousel-title-align">{title[lang]}</h2></LinkComponent>
      : <h2 className="carousel-title-align">{title[lang]}</h2>
    : null

  const descEl = description?.[lang]?.trim()
    ? <p className="carousel-description-align">{description[lang]}</p>
    : null

  if (!mounted) {
    const preview = districts.slice(0, Math.ceil(sliderCfg.desktop ?? 5))
    return (
      <section className="carousel-wrapper lg:pl-0 pl-3">
        {titleEl}{descEl}
        <div className="flex flex-row" style={{ gap: `${sliderCfg.spaceBetween ?? 16}px` }}>
          {preview.map((d, i) => (
            <DistrictCard key={i} district={d} cfg={cfg} lang={lang} />
          ))}
        </div>
      </section>
    )
  }

  return (
    <section className="carousel-wrapper w-full lg:pl-0 pl-3">
      {titleEl}{descEl}
      <Swiper
        spaceBetween={sliderCfg.spaceBetween ?? 16}
        navigation
        modules={[Navigation]}
        slidesPerView={sliderCfg.mobile ?? 1.2}
        breakpoints={{
          768:  { slidesPerView: sliderCfg.tablet  ?? 3 },
          1024: { slidesPerView: sliderCfg.desktop ?? 5 },
        }}
      >
        {districts.map((d, i) => (
          <SwiperSlide key={i}>
            <DistrictCard district={d} cfg={cfg} lang={lang} />
          </SwiperSlide>
        ))}
      </Swiper>
    </section>
  )
}

export default DistrictCardSlider